import React, { useState } from "react";
import { View, Text, StyleSheet, Image, TouchableOpacity, ScrollView } from "react-native";
import NavButton from "./NavButton";
import UserProfile from "./UserProfile";
import Notification from "./Notification"; // Assuming Notification component is in the same directory or update path

const SharedHeader = ({
	user,
	onLogout,
	onNavigateToSettings,
	notifications,
	onDismissNotification,
	onNavigateToProjects, // Specific prop for Projects navigation
	isProjectsActive, // To determine if the Projects button is active
	children, // For page-specific navigation elements
}) => {
	const [scrollMetrics, setScrollMetrics] = useState({
		offsetX: 0,
		contentWidth: 0,
		containerWidth: 0,
	});

	const handleScroll = (event) => {
		setScrollMetrics((prev) => ({ ...prev, offsetX: event.nativeEvent.contentOffset.x }));
	};

	const handleContentSizeChange = (width) => {
		setScrollMetrics((prev) => ({ ...prev, contentWidth: width }));
	};

	const handleLayout = (event) => {
		setScrollMetrics((prev) => ({ ...prev, containerWidth: event.nativeEvent.layout.width }));
	};

	// Determine if fades should be shown based on scroll metrics
	// A small threshold (e.g., 1 pixel) is used to avoid showing fade for negligible scroll amounts
	const showLeftFade = scrollMetrics.contentWidth > scrollMetrics.containerWidth && scrollMetrics.offsetX > 1;
	const showRightFade = scrollMetrics.contentWidth > scrollMetrics.containerWidth && scrollMetrics.offsetX < scrollMetrics.contentWidth - scrollMetrics.containerWidth - 1;

	return (
		<View style={styles.header}>
			<View style={styles.headerLeft}>
				<View style={styles.logoContainer}>
					<Image source={require("../assets/logosolid.png")} style={styles.logoImage} />
					<Text style={styles.logoText}>Executable</Text>
				</View>
				<View style={styles.navDivider} />
				<View style={styles.navigationContainer}>
					<ScrollView
						horizontal
						showsHorizontalScrollIndicator={false}
						contentContainerStyle={styles.scrollableNavItems}
						onScroll={handleScroll}
						onContentSizeChange={handleContentSizeChange}
						onLayout={handleLayout}
						scrollEventThrottle={16} // For smoother scroll tracking
					>
						<NavButton icon="folder-outline" label="Projects" isActive={isProjectsActive} onClick={onNavigateToProjects} />
						{children}
					</ScrollView>
					{showLeftFade && <View style={[styles.fadeEffect, styles.leftFade]} />}
					{showRightFade && <View style={[styles.fadeEffect, styles.rightFade]} />}
				</View>
			</View>

			<View style={styles.headerRight}>
				<Notification notifications={notifications} onDismiss={onDismissNotification} />
				<UserProfile user={user} onLogout={onLogout} onNavigateToSettings={onNavigateToSettings} />
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	header: {
		padding: 16,
		paddingHorizontal: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#2A2A2A",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#1A1A1A",
		position: "sticky", // For web, ensure your bundler/setup supports this
		top: 0,
		zIndex: 1000, // Ensure header is above other content
		overflow: "visible",
	},
	headerLeft: {
		flexDirection: "row",
		alignItems: "center",
		flex: 1,
		// gap: 24, // Removed main gap, applying specific margins below
	},
	headerRight: {
		flexDirection: "row",
		alignItems: "center",
		gap: 20,
	},
	logoContainer: {
		flexDirection: "row",
		alignItems: "center",
		// gap: 0, // No gap between image and text for the logo itself
		marginRight: 24, // Space after the logo block
	},
	logoImage: {
		width: 32,
		height: 32,
		resizeMode: "contain",
	},
	logoText: {
		fontSize: 20,
		fontWeight: "bold",
		color: "#fff",
		letterSpacing: -0.5,
	},
	navDivider: {
		width: 1,
		height: 28,
		backgroundColor: "#2A2A2A",
		marginRight: 16, // Space after the divider, reduced from 24 to give more typical nav spacing
	},
	navigationContainer: {
		flex: 1, // Takes up available space within headerLeft
		overflow: "hidden", // Clips ScrollView content
		position: "relative", // For absolute positioning of fade overlays
		// Removed flexDirection: "row" and alignItems: "center" as ScrollView is the main child
	},
	scrollableNavItems: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8, // Gap between items in the ScrollView
	},
	fadeEffect: {
		position: "absolute",
		top: 0,
		bottom: 0,
		width: 30, // Width of the fade area
		backgroundColor: "rgba(26, 26, 26, 0.75)", // Semi-transparent version of header background (#1A1A1A)
		pointerEvents: "none", // Allows touch events to pass through to the ScrollView
	},
	leftFade: {
		left: 0,
	},
	rightFade: {
		right: 0,
	},
	// Styles for NavButton and UserProfile are in their respective components
	// Styles for Notification are assumed to be in its component
});

export default SharedHeader;
